<template>
  <div class="page-outer">
    <div class="page-header page-header-fixed-form">
      <div class="header-left">
        <h1 class="page-title">{{ pageTitle }}</h1>
      </div>
      <div class="header-actions">
        <button class="btn btn-primary" @click="handleGoToBatchIssue">
                <i class="ri-mail-send-line"></i>
                批次發送優惠券
              </button>
      </div>
    </div>

    <div class="page-container">
    <div class="page-toolbar">
      <div class="search-box">
        <input
          v-model="searchQuery"
          type="text"
          class="form-control"
          placeholder="搜尋批次 ID、模板名稱或發放原因..."
          @input="handleSearch"
        />
        <i class="ri-search-line"></i>
      </div>
      <div class="filter-group">
        <select v-model="filterStatus" class="form-control" @change="handleFilterChange">
          <option value="">全部狀態</option>
          <option value="processing">執行中</option>
          <option value="completed">已完成</option>
          <option value="failed">失敗</option>
          <option value="cancelled">已取消</option>
        </select>
        <select v-model="filterTemplate" class="form-control" @change="handleFilterChange">
          <option value="">全部模板</option>
          <option v-for="template in templates" :key="template.id" :value="template.id">
            {{ template.templateName }}
          </option>
        </select>
      </div>
    </div>

    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
      <p>載入中...</p>
    </div>

    <div v-else-if="error" class="error-state">
      <p class="error-message">{{ error }}</p>
      <button class="btn btn-secondary" @click="fetchBatchIssuances">重試</button>
    </div>

    <div v-else class="table-wrapper">
      <table>
        <thead>
          <tr>
            <th>批次 ID</th>
            <th>優惠券模板</th>
            <th>狀態</th>
            <th>對象類型</th>
            <th>發送對象數</th>
            <th>總發放數</th>
            <th>成功/失敗</th>
            <th>建立時間</th>
            <th>操作</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="batchIssuances.length === 0">
            <td colspan="9" class="empty-state">
              沒有找到批次發放優惠券
            </td>
          </tr>
          <tr v-for="issuance in batchIssuances" :key="issuance.id">
            <td>
              <code class="code-badge">{{ issuance.batchId }}</code>
            </td>
            <td>{{ issuance.templateName }}</td>
            <td>
              <span :class="getStatusBadgeClass(issuance.status)">
                {{ getStatusText(issuance.status) }}
              </span>
              <span v-if="issuance.revokedAt" class="badge badge-revoked">
                已撤銷
              </span>
            </td>
            <td>
              <span :class="getTargetTypeBadgeClass(issuance.targetType)">
                {{ getTargetTypeText(issuance.targetType) }}
              </span>
            </td>
            <td>{{ issuance.totalMembers || 0 }}</td>
            <td>{{ issuance.totalCoupons || 0 }}</td>
            <td>
              <span v-if="issuance.status === 'completed'">
                {{ issuance.successCount || 0 }} / {{ issuance.failedCount || 0 }}
              </span>
              <span v-else>-</span>
            </td>
            <td>{{ formatDateTime(issuance.createdAt) }}</td>
            <td>
              <div class="action-buttons">
                <button class="btn btn-sm btn-secondary" @click="handleView(issuance.batchId)">
                  <i class="ri-eye-line"></i>
                  查看
                </button>
                <button
                  class="btn btn-sm btn-warning"
                  :disabled="actionLoading || issuance.status === 'processing' || !!issuance.revokedAt"
                  @click="handleRevoke(issuance)"
                  :title="issuance.revokedAt ? '已撤銷' : '撤銷已發票券（軟刪除，保留批次紀錄）'"
                >
                  <i class="ri-forbidden-line"></i>
                  撤銷票券
                </button>
                <button class="btn btn-sm btn-danger" :disabled="actionLoading" @click="handleDelete(issuance.batchId)">
                  <i class="ri-delete-bin-line"></i>
                  刪除紀錄
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>

      <!-- 分頁 -->
      <ListPagination
        v-if="pagination && pagination.totalPages > 1"
        :page="pagination.page"
        :total-pages="pagination.totalPages"
        :total="pagination.totalItems"
        @change="changePage"
      />
    </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, onBeforeUnmount } from 'vue';
import { useRouter } from 'vue-router';
import { usePageTitle } from '@/composables/usePageTitle.js';
import { couponService } from '@/api/modules/coupons.js';
import { showError, showSuccess } from '@/utils/swal.js';
import Swal from 'sweetalert2';

export default {
  name: 'BatchCouponIssuances',
  setup() {
    const router = useRouter();
    const { pageTitle } = usePageTitle('批次發送優惠券');
    const batchIssuances = ref([]);
    const loading = ref(false);
    const error = ref(null);
    const pagination = ref(null);
    const searchQuery = ref('');
    const filterStatus = ref('');
    const filterTemplate = ref('');
    const templates = ref([]);
    const searchTimeout = ref(null);
    const actionLoading = ref(false);
    let isUnmounted = false;

    const fetchTemplates = async () => {
      try {
        const response = await couponService.getTemplates({ pageSize: 100, isActive: true });
        if (response && response.success && response.data) {
          templates.value = response.data.items || [];
        }
      } catch (err) {
        console.error('獲取優惠券模板失敗:', err);
      }
    };

    const fetchBatchIssuances = async () => {
      loading.value = true;
      error.value = null;

      try {
        const params = {
          page: pagination.value?.page || 1,
          pageSize: 20
        };

        if (searchQuery.value) {
          params.search = searchQuery.value;
        }
        if (filterStatus.value) {
          params.status = filterStatus.value;
        }
        if (filterTemplate.value) {
          params.templateId = filterTemplate.value;
        }

        const response = await couponService.getBatchIssuances(params);

        if (isUnmounted) return;

        if (response && response.success && response.data) {
          batchIssuances.value = response.data.items || [];
          pagination.value = response.data.pagination;
        } else {
          error.value = response?.message || '獲取批次發放優惠券列表失敗';
        }
      } catch (err) {
        if (isUnmounted) return;
        console.error('獲取批次發放優惠券列表失敗:', err);
        error.value = err.message || '獲取批次發放優惠券列表失敗';
        await showError('載入失敗', error.value);
      } finally {
        if (!isUnmounted) {
          loading.value = false;
        }
      }
    };

    const handleSearch = () => {
      if (searchTimeout.value) {
        clearTimeout(searchTimeout.value);
      }
      searchTimeout.value = setTimeout(() => {
        if (pagination.value) {
          pagination.value.page = 1;
        }
        fetchBatchIssuances();
      }, 500);
    };

    const handleFilterChange = () => {
      if (pagination.value) {
        pagination.value.page = 1;
      }
      fetchBatchIssuances();
    };

    const changePage = (page) => {
      if (pagination.value) {
        pagination.value.page = page;
      }
      fetchBatchIssuances();
    };

    const handleView = (batchId) => {
      router.push(`/client/coupons/issues/batch/${batchId}`);
    };

    const getTargetTypeText = (targetType) => {
      const map = {
        member_list: '會員',
        all_friends: 'LINE 好友（全部）',
        line_tags: 'LINE 好友（標籤）'
      };
      return map[targetType] || targetType || '-';
    };

    const getTargetTypeBadgeClass = (targetType) => {
      if (targetType === 'member_list') return 'badge badge-secondary';
      return 'badge badge-line';
    };

    const getStatusText = (status) => {
      const statusMap = {
        processing: '執行中',
        completed: '已完成',
        failed: '失敗',
        cancelled: '已取消'
      };
      return statusMap[status] || status;
    };

    const getStatusBadgeClass = (status) => {
      const classMap = {
        processing: 'badge badge-info',
        completed: 'badge badge-success',
        failed: 'badge badge-danger',
        cancelled: 'badge badge-secondary'
      };
      return classMap[status] || 'badge badge-secondary';
    };

    const formatDateTime = (dateString) => {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return date.toLocaleString('zh-TW', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    };

    const handleGoToBatchIssue = () => {
      router.push('/client/coupons/issues/batch/create');
    };

    const handleRevoke = async (issuance) => {
      const result = await Swal.fire({
        icon: 'warning',
        title: '確認撤銷已發票券',
        html: `
          <div style="text-align: left; padding: 0 8px;">
            <p style="margin: 0 0 12px 0; font-size: 15px; color: #333;">
              批次：<code style="font-size: 13px;">${issuance.batchId}</code>
            </p>
            <p style="margin: 0 0 16px 0; font-size: 14px; color: #555;">
              將把此批次發出的票券狀態標記為「已刪除」（已使用的票券不受影響），
              <strong>批次作業紀錄會保留</strong>。
            </p>
            <div style="background: #fff3cd; border-left: 4px solid #ffc107; padding: 10px 14px; border-radius: 4px; font-size: 13px; color: #856404;">
              此操作無法復原，請確認後再繼續。
            </div>
          </div>
        `,
        showCancelButton: true,
        confirmButtonText: '確認撤銷',
        cancelButtonText: '取消',
        confirmButtonColor: '#e0a800',
        cancelButtonColor: '#6c757d',
        reverseButtons: true,
        width: '480px'
      });

      if (!result.isConfirmed) return;

      actionLoading.value = true;
      try {
        const response = await couponService.revokeIssuedCoupons(issuance.batchId);
        if (response && response.success) {
          showSuccess(`撤銷完成，共撤銷 ${response.data?.revokedCount ?? 0} 張票券`);
          fetchBatchIssuances();
        } else {
          showError(response?.message || '撤銷失敗');
        }
      } catch (err) {
        console.error('撤銷票券失敗:', err);
        showError(err.message || '撤銷失敗');
      } finally {
        actionLoading.value = false;
      }
    };

    const handleDelete = async (batchId) => {
      const confirmed = await Swal.fire({
        icon: 'question',
        title: '確認刪除批次紀錄',
        html: `
          <div style="text-align: left; padding: 0 8px;">
            <p style="margin: 0 0 12px 0; font-size: 14px; color: #555;">
              批次：<code style="font-size: 13px;">${batchId}</code>
            </p>
            <p style="margin: 0 0 16px 0; font-size: 14px; color: #555;">
              僅刪除此批次作業紀錄，<strong>已發出的票券不受影響</strong>，仍可在優惠券發放紀錄中查閱。
            </p>
            <div style="background: #f8d7da; border-left: 4px solid #dc3545; padding: 10px 14px; border-radius: 4px; font-size: 13px; color: #721c24;">
              刪除後此批次紀錄將無法恢復。
            </div>
          </div>
        `,
        showCancelButton: true,
        confirmButtonText: '確認刪除紀錄',
        cancelButtonText: '取消',
        confirmButtonColor: '#dc3545',
        cancelButtonColor: '#6c757d',
        reverseButtons: true,
        width: '480px'
      });

      if (!confirmed.isConfirmed) return;

      actionLoading.value = true;
      try {
        const response = await couponService.deleteBatchIssuance(batchId);
        if (response && response.success) {
          showSuccess('批次紀錄已刪除');
          fetchBatchIssuances();
        } else {
          showError(response?.message || '刪除失敗');
        }
      } catch (err) {
        console.error('刪除批次紀錄失敗:', err);
        showError(err.message || '刪除失敗');
      } finally {
        actionLoading.value = false;
      }
    };

    onMounted(() => {
      fetchTemplates();
      fetchBatchIssuances();
    });

    onBeforeUnmount(() => {
      isUnmounted = true;
      if (searchTimeout.value) {
        clearTimeout(searchTimeout.value);
        searchTimeout.value = null;
      }
    });

    return {
      pageTitle,
      batchIssuances,
      loading,
      error,
      pagination,
      searchQuery,
      filterStatus,
      filterTemplate,
      templates,
      fetchBatchIssuances,
      handleSearch,
      handleFilterChange,
      changePage,
      handleView,
      handleGoToBatchIssue,
      handleRevoke,
      handleDelete,
      getTargetTypeText,
      getTargetTypeBadgeClass,
      getStatusText,
      getStatusBadgeClass,
      formatDateTime
    };
  }
};
</script>

<style scoped>
@import '~@/assets/scss/components/_list-page-common.scss';

.page-toolbar {
  flex-wrap: wrap;
}

.search-box {
  position: relative;
  flex: 1;
  min-width: 200px;
}

.search-box input {
  padding-right: 40px;
}

.search-box i {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #999;
  pointer-events: none;
}

.filter-group {
  display: flex;
  gap: 12px;
}

.filter-group select {
  min-width: 150px;
}

/* 覆寫表格最小寬度（11 個欄位需要更大的寬度） */
.table-wrapper table {
  min-width: 1350px;
}

.badge-line {
  background: #06c755;
  color: #fff;
  padding: 3px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
}

thead {
  background: #f8f9fa;
}

th {
  padding: 12px;
  text-align: left;
  font-weight: 600;
  color: #333;
  border-bottom: 2px solid #e9ecef;
}

td {
  padding: 12px;
  border-bottom: 1px solid #e9ecef;
}

tbody tr:hover {
  background: #f8f9fa;
}

.empty-state {
  text-align: center;
  padding: 40px;
  color: #999;
}

.code-badge {
  background: #f0f0f0;
  padding: 4px 8px;
  border-radius: 4px;
  font-family: monospace;
  font-size: 12px;
}

.progress-bar {
  position: relative;
  width: 100px;
  height: 20px;
  background: #e9ecef;
  border-radius: 10px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background: #28a745;
  transition: width 0.3s;
}

.progress-text {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 11px;
  font-weight: 600;
  color: #333;
}

.action-buttons {
  display: flex;
  gap: 8px;
}

.badge-revoked {
  display: inline-block;
  margin-top: 4px;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  background: #6c757d;
  color: #fff;
}

.revoke-info {
  margin-top: 4px;
  font-size: 12px;
  color: #6c757d;
  line-height: 1.5;
}

.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 16px;
  padding: 20px;
}

.pagination-info {
  color: #666;
  font-size: 14px;
}
</style>
